import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Search, ArrowRight, Target, TrendingDown, Wallet } from "lucide-react";
import { PROPERTY_TYPES } from "../lib/propertyTypes";
import { DISTRESS_SIGNALS } from "../lib/distressSignals";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const QUICK_PICKS = [
  { city: "Birmingham", county: "Jefferson", state: "AL" },
  { city: "Memphis", county: "Shelby", state: "TN" },
  { city: "Cleveland", county: "Cuyahoga", state: "OH" },
  { city: "Detroit", county: "Wayne", state: "MI" },
  { city: "Indianapolis", county: "Marion", state: "IN" },
  { city: "Kansas City", county: "Jackson", state: "MO" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [state, setState] = useState("");
  const [types, setTypes] = useState([]);
  const [signals, setSignals] = useState([]);

  const toggleType = (id) =>
    setTypes((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toggleSignal = (id) =>
    setSignals((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const go = (c, ct, s, ts = types, sigs = signals) => {
    if (!c.trim() || !s) {
      toast.error("Enter a city and select a state");
      return;
    }
    const params = new URLSearchParams({ city: c.trim(), county: ct.trim(), state: s });
    if (ts && ts.length > 0) params.set("types", ts.join(","));
    if (sigs && sigs.length > 0) params.set("signals", sigs.join(","));
    nav(`/market?${params.toString()}`);
  };

  return (
    <Layout>
      <div className="px-8 py-10 max-w-6xl mx-auto">
        <div className="overline mb-4">// 01 · Recon</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] mb-4">
          Where<br/>nobody<br/>else is <span className="text-[--accent]">looking.</span>
        </h1>
        <p className="text-[--ink-soft] max-w-xl text-base mb-10">
          Type a city, county, and state. Get a saturation score, motivation index, cash transaction
          density, and a ranked list of deals — with MAO and ARV already calculated.
        </p>

        <form
          data-testid="market-search-form"
          onSubmit={(e) => {
            e.preventDefault();
            go(city, county, state);
          }}
          className="surface-card p-6 mb-12 animate-fade-up"
        >
          <div className="grid md:grid-cols-12 gap-4">
            <div className="md:col-span-5">
              <label className="overline block mb-2">City</label>
              <input
                data-testid="search-city-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Birmingham"
                className="w-full"
                required
              />
            </div>
            <div className="md:col-span-4">
              <label className="overline block mb-2">County</label>
              <input
                data-testid="search-county-input"
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                placeholder="e.g. Jefferson"
                className="w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">State</label>
              <select
                data-testid="search-state-select"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                className="w-full"
              >
                <option value="">—</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1 flex items-end">
              <button
                data-testid="search-submit-button"
                type="submit"
                className="btn-primary w-full flex items-center justify-center gap-1.5"
                aria-label="Run analysis"
              >
                <Search size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-[--line]">
            <label className="overline block mb-2">
              Property types{" "}
              <span className="text-[--ink-muted] normal-case tracking-normal">
                · leave empty for all
              </span>
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="search-types">
              {PROPERTY_TYPES.map((t) => {
                const on = types.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`search-type-${t.id}`}
                    onClick={() => toggleType(t.id)}
                    className={`text-xs px-3 py-1.5 border transition-colors ${
                      on
                        ? "bg-[--ink] text-white border-[--ink]"
                        : "bg-white text-[--ink] border-[--line] hover:border-[--ink]"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-[--line]">
            <label className="overline block mb-2">
              Distress signals{" "}
              <span className="text-[--ink-muted] normal-case tracking-normal">
                · match any selected
              </span>
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="search-signals">
              {DISTRESS_SIGNALS.map((s) => {
                const on = signals.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`search-signal-${s.id}`}
                    onClick={() => toggleSignal(s.id)}
                    className={`text-xs px-3 py-1.5 border transition-colors ${
                      on
                        ? "bg-[--accent] text-white border-[--accent]"
                        : "bg-white text-[--ink] border-[--line] hover:border-[--accent]"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </form>

        <div className="mb-10">
          <div className="overline mb-3">Quick picks · proven distressed markets</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {QUICK_PICKS.map((q) => (
              <button
                key={`${q.city}-${q.state}`}
                data-testid={`quick-pick-${q.city.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => go(q.city, q.county, q.state)}
                className="surface-card p-4 text-left hover:bg-[--bg-soft] transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{q.city}, {q.state}</div>
                    <div className="text-xs text-[--ink-muted] font-mono">{q.county} County</div>
                  </div>
                  <ArrowRight size={14} className="text-[--ink-muted] group-hover:text-[--ink] transition-colors" strokeWidth={1.5} />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 border-t border-[--line] pt-10">
          {[
            { Icon: Target, title: "Motivation Index", body: "Composite score from pre-foreclosures, evictions, code violations, absentee owners, and tax delinquency." },
            { Icon: TrendingDown, title: "Saturation Score", body: "How crowded the market is with other investors. Lower = more whitespace for you." },
            { Icon: Wallet, title: "Cash Landlord %", body: "Share of recent transactions closed in cash by landlord buyers — a tell for active inventory turnover." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="surface-card p-5">
              <Icon size={18} strokeWidth={1.5} className="text-[--accent] mb-3" />
              <div className="font-display font-bold text-base mb-1.5">{title}</div>
              <div className="text-xs text-[--ink-soft] leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
